import { TrackClickDirective } from '@/directives/track-click/track-click.directive';
import { injectModalInput } from '@/services/advizeo-modal.service';
import { SharedUiBackgroundColorName, type SharedUiBackgroundColorOptions, type SharedUiColorNameV2 } from '@/shared/ui/theming';
import { type ButtonStyle, UiButtonComponent } from '@/ui/button/ui-button';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import type { IconName } from '@/ui/icon/ui-icon';
import { type Branded } from '@/utils/typing/branded.type';
import {
    ChangeDetectionStrategy,
    Component,
    type ComponentRef,
    InjectionToken,
    Injector,
    type OnInit,
    type OutputEmitterRef,
    type Signal,
    type Type,
    ViewChild,
    ViewContainerRef,
    assertInInjectionContext,
    computed,
    inject,
    output,
    outputBinding,
    signal
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { type ModalComponent } from '../../interfaces/Modal.interface';
import { SharedUiBackgroundColorDirective } from '../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';

export interface AdvizeoModalButton {
    text: string;
    onClick: () => void;
    isDisabled?: boolean;
    color: SharedUiColorNameV2;
    buttonStyle?: ButtonStyle;
    isPending?: boolean;
    iconName?: IconName;
    trackClick?: string;
    anchor?: string; // Using by behavior tests, add attribute to target this button
}

export interface AdvizeoModalSpawnerConfig<Input, Output> {
    modalComponent: Type<AdvizeoModalComponent<Input, Output>>;
    modalComponentInput: Input;
}

export interface AdvizeoModalComponent<Input, Output> {
    title: Signal<string>;
    isDismissable: Signal<boolean>;
    buttons: Signal<AdvizeoModalButton[]>;
    input: InjectAdvizeoModalInput<Input>;
    close: OutputEmitterRef<Output extends null | undefined ? void : Output | void | undefined>;
    background?: SharedUiBackgroundColorOptions;
}

export const ADVIZEO_MODAL_COMPONENT_INPUT_DATA = new InjectionToken('ADVIZEO_MODAL_COMPONENT_INPUT_DATA');

type InjectAdvizeoModalInput<T> = Branded<Signal<T>, 'InjectAdvizeoModalInput'>;

export function injectAdvizeoModalInput<T>(): InjectAdvizeoModalInput<T> {
    assertInInjectionContext(injectAdvizeoModalInput);
    const data = inject<T>(ADVIZEO_MODAL_COMPONENT_INPUT_DATA);
    return signal(data).asReadonly() as InjectAdvizeoModalInput<T>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./advizeo-modal-spawner.component.scss'],
    selector: 'advizeo-modal-spawner',
    templateUrl: 'advizeo-modal-spawner.component.html',
    imports: [UiIconButtonComponent, SharedUiBackgroundColorDirective, UiButtonComponent, TranslatePipe, TrackClickDirective]
})
export class AdvizeoModalSpawnerComponent<Input, Output>
    implements ModalComponent<AdvizeoModalSpawnerConfig<Input, Output>, Output>, OnInit
{
    private injector = inject(Injector);

    input = injectModalInput<AdvizeoModalSpawnerConfig<Input, Output>>();
    close = output<Output | undefined>();

    modalComponent = signal<AdvizeoModalComponent<Input, Output> | null>(null);
    buttons = computed(() => this.modalComponent()?.buttons().slice().reverse() ?? []);

    DEFAULT_BG_COLOR = SharedUiBackgroundColorName.LIGHT;

    @ViewChild('modalHost', { read: ViewContainerRef, static: true })
    private modalHost!: ViewContainerRef;

    private componentRef?: ComponentRef<AdvizeoModalComponent<Input, Output>>;

    ngOnInit() {
        const childInjector = Injector.create({
            providers: [{ provide: ADVIZEO_MODAL_COMPONENT_INPUT_DATA, useValue: this.input.modalComponentInput }],
            parent: this.injector
        });

        this.componentRef = this.modalHost.createComponent(this.input.modalComponent, {
            injector: childInjector,
            bindings: [outputBinding('close', event => this.close.emit(event as Output | undefined))]
        });
        this.modalComponent.set(this.componentRef.instance);
    }
}
